// "Needs cleaning" — companies and engagements where the stage-signal
// pre-cleaner found mismatches it couldn't auto-resolve. Two-tab view,
// grouped by reason. Clicking a row opens the entity in its detail page;
// editing the entity removes it from the queue on the next refresh.

const REASON_HUE = {
  // Severity 3 = likely-wrong
  deep_signal_negative_tracker: 'rose',
  loi_signal_wrong_stage:       'rose',
  tier1_inferred_higher:        'rose',
  multi_tier_consensus:         'rose',
  // Severity 2 = recommended
  signal_but_no_tracker:        'amber',
  ds_call_still_outreach:       'amber',
  bs_signal_early_stage:        'amber',
  closing_stalled:              'amber',
  // Severity 1 = FYI
  terminal_no_signals:          'slate',
};

const REASON_LABEL = {
  deep_signal_negative_tracker: 'Deep evidence vs. "not interested" tracker',
  loi_signal_wrong_stage:       'LOI evidence but stage isn\'t LOI/Won',
  tier1_inferred_higher:        'Notes suggest a higher stage than recorded',
  multi_tier_consensus:         'Multiple methods agree on a different stage',
  signal_but_no_tracker:        'Has signals but no tracker set',
  ds_call_still_outreach:       'D/S call happened but tracker still "Outreach"',
  bs_signal_early_stage:        'B/S call evidence but stage is pre-BS',
  closing_stalled:              'Closing stalled (no Won, no exit)',
  terminal_no_signals:          'Tracker = Closed/Sold but no evidence',
};

// Tabs that own their data (they render their own pane and must not trip the
// shared companies/engagements fetch or its empty state).
const _DERIVED_TABS = new Set(['duplicates', 'industries', 'blast', 'stale', 'holds']);
// Tabs a link can open directly: #/cleanup?tab=stale, #/cleanup?tab=holds.
const _LINKABLE_TABS = new Set(['stale', 'holds']);
const tabFromHash = () => {
  const m = /[?&]tab=([a-z_]+)/.exec(window.location.hash || '');
  return m && _LINKABLE_TABS.has(m[1]) ? m[1] : 'companies';
};

const CleanupView = ({ setView, setDealId, setCompanyId, setContactId, currentRole }) => {
  const [tab, setTab]       = React.useState(tabFromHash); // companies | engagements | duplicates | industries | blast | stale | holds
  const [data, setData]     = React.useState(null);
  const [groupFilter, setGroupFilter] = React.useState(null);
  const [summary, setSummary] = React.useState(null);

  React.useEffect(() => {
    window.AtlasApi.cleanupSummary().then(setSummary).catch(() => setSummary(null));
  }, []);

  React.useEffect(() => {
    setData(null);
    if (_DERIVED_TABS.has(tab)) return;  // these panes fetch their own data
    const fn = tab === 'companies' ? window.AtlasApi.cleanupCompanies : window.AtlasApi.cleanupEngagements;
    const params = groupFilter ? { reason: groupFilter } : {};
    fn(params).then(setData).catch(() => setData({ items: [], total: 0, groups: {} }));
  }, [tab, groupFilter]);

  const groups = (summary && !_DERIVED_TABS.has(tab))
    ? (tab === 'companies' ? summary.company_groups : summary.engagement_groups) || {}
    : {};

  // Row click: jump to the entity using the existing per-type navigators.
  const openItem = (it) => {
    if (it.entity_type === 'engagement') setDealId && setDealId(it.id);
    else if (it.entity_type === 'company') setCompanyId && setCompanyId(it.id);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Needs cleaning']}
        title="Needs cleaning"
        meta={summary
          ? `${summary.total.toLocaleString()} rows · ${summary.companies} companies · ${summary.engagements} engagements`
          : 'Loading…'}
      />

      {/* Tab bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '0 16px',
        borderBottom: '1px solid var(--border)', background: 'var(--surface)', height: 36,
      }}>
        {[
          ['companies',   'Companies',   summary?.companies],
          ['engagements', 'Engagements', summary?.engagements],
          ['duplicates',  'Duplicates',  null],
          ['industries',  'Industries',  null],
          ['blast',       'Blast hygiene', null],
          ['stale',       'Stale deals', null],
          ['holds',       'Holds', null],
        ].map(([k, label, count]) => {
          const active = tab === k;
          return (
            <button key={k} onClick={() => { setTab(k); setGroupFilter(null); }} style={{
              padding: '0 10px', height: 36, border: 'none', background: 'transparent',
              fontSize: 12.5, fontWeight: active ? 600 : 500, cursor: 'pointer',
              color: active ? 'var(--text)' : 'var(--text-3)',
              borderBottom: active ? '2px solid var(--text)' : '2px solid transparent',
              marginBottom: -1, display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              {label}
              {count != null && <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Group filter row */}
      {Object.keys(groups).length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 4,
          padding: '8px 16px', borderBottom: '1px solid var(--border)',
          background: 'var(--surface-2)',
        }}>
          <button onClick={() => setGroupFilter(null)} style={chipStyle(groupFilter == null)}>
            All <span style={chipCountStyle()}>{Object.values(groups).reduce((a, b) => a + b, 0)}</span>
          </button>
          {Object.entries(groups).sort((a, b) => b[1] - a[1]).map(([code, n]) => (
            <button key={code} onClick={() => setGroupFilter(code === groupFilter ? null : code)}
              style={chipStyle(groupFilter === code)}
              title={REASON_LABEL[code] || code}>
              {REASON_LABEL[code] || code} <span style={chipCountStyle()}>{n}</span>
            </button>
          ))}
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto', padding: 16, background: 'var(--surface-2)' }}>
        {tab === 'duplicates' && <DuplicatesPane setCompanyId={setCompanyId} setContactId={setContactId} setDealId={setDealId}
          isAdmin={(currentRole || '').toLowerCase() === 'admin'} />}
        {tab === 'industries' && <IndustryProposalsPane setCompanyId={setCompanyId} />}
        {tab === 'blast' && <BlastHygienePane setContactId={setContactId} />}
        {tab === 'stale' && <StaleDealsPane setDealId={setDealId} />}
        {tab === 'holds' && <HoldsPane setDealId={setDealId}
          canEdit={['admin', 'editor'].includes((currentRole || '').toLowerCase())} />}
        {!_DERIVED_TABS.has(tab) && data === null && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
        {!_DERIVED_TABS.has(tab) && data && data.items.length === 0 && (
          <div style={{
            background: 'var(--surface)', border: '1px dashed var(--border-strong)', borderRadius: 10,
            padding: '32px 24px', maxWidth: 640, fontSize: 12.5, color: 'var(--text-3)',
          }}>
            {groupFilter
              ? `No rows match "${REASON_LABEL[groupFilter] || groupFilter}".`
              : 'Nothing needs cleaning — every entity in scope passes its rules.'}
          </div>
        )}
        {!_DERIVED_TABS.has(tab) && data && data.items.length > 0 && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {data.items.map((it, i) => <CleanupRow key={it.id} item={it} isFirst={i === 0} onOpen={openItem} />)}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Duplicates — one queue for companies, people and deals (routes/dedup_queue.py,
// filled nightly by app/dedup_scan.py). A person decides every pair: keep one
// side (the other is merged into it — its own merge, so the same guards and,
// when both are in DealCloud, the same DealCloud merge) or "Not duplicates",
// which is remembered. Admins also get "Merge all strong" over a reviewed,
// ticked selection, and Rescan.
const DEDUP_TYPES = [['company', 'Companies'], ['contact', 'People'], ['engagement', 'Deals']];
const DEDUP_REASON = {
  same_domain: 'Same website', same_name: 'Same name', same_email: 'Same email',
  same_name_at_company: 'Same name at the same company', same_company_live_deals: 'Two live deals at one company',
  dismissed_before: 'Dismissed before',
};
const DEDUP_NOUN = { company: ['company', 'companies'], contact: ['person', 'people'], engagement: ['deal', 'deals'] };

const DedupSide = ({ type, rec, suggested, onOpen, onKeep, busy }) => (
  <div style={{ flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 6,
                border: `1px solid ${suggested ? 'var(--accent-border, var(--accent))' : 'var(--border)'}`,
                background: 'var(--surface)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <button onClick={onOpen} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
        fontSize: 13, fontWeight: 600, color: 'var(--text)', textAlign: 'left' }}>{rec.title || '(no name)'}</button>
      {suggested && <Pill hue="blue" sz="sm">suggested keep</Pill>}
      {rec.dealcloud_id && <Pill hue="slate" sz="sm">ID {rec.dealcloud_id}</Pill>}
      {rec.is_buyer && <Pill hue="violet" sz="sm">buyer</Pill>}
    </div>
    <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 3, lineHeight: 1.55 }}>
      {[rec.sub, rec.place, rec.state].filter(Boolean).join(' · ') || '—'}
    </div>
    <div style={{ fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.55 }}>
      {[rec.owner && `Owner ${rec.owner}`,
        type === 'company' && `${rec.n_contacts} people · ${rec.n_deals} deals · ${rec.n_notes} notes`,
        type === 'contact' && `${rec.n_notes} notes${rec.phone ? ` · ${rec.phone}` : ''}`,
        type === 'engagement' && `${rec.n_notes} notes · ${rec.n_buyers} buyers`,
        rec.created && `created ${window.fmtDate(rec.created)}`].filter(Boolean).join(' · ')}
    </div>
    <div style={{ marginTop: 8 }}>
      <Btn size="xs" variant={suggested ? 'accent' : 'outline'} disabled={busy} onClick={onKeep}>Keep this one</Btn>
    </div>
  </div>
);

const DuplicatesPane = ({ setCompanyId, setContactId, setDealId, isAdmin }) => {
  const [type, setType] = React.useState('company');
  const [strength, setStrength] = React.useState('');
  const [items, setItems] = React.useState(null);
  const [counts, setCounts] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [bulk, setBulk] = React.useState(null);     // {pairs, off: Set, running, results}
  const [scanning, setScanning] = React.useState(false);
  const [one, many] = DEDUP_NOUN[type];

  const load = React.useCallback(() => {
    setItems(null);
    window.AtlasApi.dedupQueue({ type, strength: strength || undefined, limit: 200 })
      .then((r) => setItems((r && r.items) || [])).catch(() => setItems([]));
    window.AtlasApi.dedupQueueCounts().then(setCounts).catch(() => {});
  }, [type, strength]);
  React.useEffect(() => { load(); }, [load]);

  const open = (rec) => (type === 'company' ? setCompanyId : type === 'contact' ? setContactId : setDealId)(rec.id);
  const drop = (id) => setItems((prev) => (prev || []).filter((x) => x.id !== id));

  const keep = async (p, keepRec, goneRec) => {
    const bothDc = Boolean(keepRec.dealcloud_id && goneRec.dealcloud_id);
    const ok = await showConfirm({
      title: `Merge into “${keepRec.title}”?`,
      body: `“${goneRec.title}” is merged into it: everything linked to it moves over and empty fields fill in. It can't be undone from the UI.`
        + (bothDc ? ' Both are in DealCloud, so they are merged there too — DealCloud deletes the duplicate.' : ''),
      confirmLabel: 'Merge', danger: true,
    });
    if (!ok) return;
    setBusy(p.id);
    try {
      await window.AtlasApi.dedupQueueMerge(p.id, keepRec.id);
      pushToast({ type: 'success', title: 'Merged', body: `${goneRec.title} → ${keepRec.title}` });
      drop(p.id);
    } catch (e) {
      pushToast({ type: 'error', title: 'Merge failed', body: e.message });
    } finally { setBusy(null); }
  };

  const dismiss = async (p) => {
    setBusy(p.id);
    try { await window.AtlasApi.dedupQueueDismiss(p.id); drop(p.id); }
    catch (e) { pushToast({ type: 'error', title: 'Could not save', body: e.message }); }
    finally { setBusy(null); }
  };

  const openBulk = async () => {
    const r = await window.AtlasApi.dedupQueue({ type, strength: 'strong', limit: 200 });
    setBulk({ pairs: (r && r.items) || [], off: new Set(), running: false, results: null });
  };
  const runBulk = async () => {
    const chosen = bulk.pairs.filter((p) => !bulk.off.has(p.id));
    if (!chosen.length) return;
    const dc = chosen.filter((p) => p.a.dealcloud_id && p.b.dealcloud_id).length;
    const ok = await showConfirm({
      title: `Merge ${chosen.length} strong ${chosen.length === 1 ? 'pair' : 'pairs'}?`,
      body: `Each keeps the suggested record and merges the other into it.`
        + (dc ? ` ${dc} ${dc === 1 ? 'pair is' : 'pairs are'} in DealCloud and will be merged there too — DealCloud deletes the duplicates, which can't be undone.` : ''),
      confirmLabel: `Merge ${chosen.length}`, danger: true,
    });
    if (!ok) return;
    setBulk((b) => ({ ...b, running: true }));
    const results = [];
    for (let i = 0; i < chosen.length; i += 50) {
      try {
        const res = await window.AtlasApi.dedupMergeStrong(chosen.slice(i, i + 50).map((p) => p.id));
        results.push(...((res && res.results) || []));
      } catch (e) {
        chosen.slice(i, i + 50).forEach((p) => results.push({ id: p.id, ok: false, error: e.message }));
      }
    }
    const merged = results.filter((r) => r.ok).length;
    pushToast({ type: merged === results.length ? 'success' : 'warning', title: `Merged ${merged} of ${results.length}` });
    setBulk((b) => ({ ...b, running: false, results }));
    load();
  };

  const rescan = async () => {
    setScanning(true);
    try {
      await window.AtlasApi.dedupScan();
      for (let i = 0; i < 120; i += 1) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await window.AtlasApi.dedupScanStatus();
        if (!s || !s.running) break;
      }
      load();
    } catch (e) {
      pushToast({ type: 'error', title: 'Rescan failed', body: e.message });
    } finally { setScanning(false); }
  };

  const n = (t, s) => (counts && counts[t] ? (s ? counts[t][s] : counts[t].strong + counts[t].review) : null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {DEDUP_TYPES.map(([k, label]) => (
          <button key={k} onClick={() => setType(k)} style={chipStyle(type === k)}>
            {label} {n(k) != null && <span style={chipCountStyle()}>{n(k)}</span>}
          </button>
        ))}
        <span style={{ width: 12 }} />
        {[['', 'All'], ['strong', 'Strong'], ['review', 'Worth a look']].map(([k, label]) => (
          <button key={k || 'all'} onClick={() => setStrength(k)} style={chipStyle(strength === k)}
            title={k === 'strong' ? 'The evidence alone says same record' : k === 'review' ? 'Similar — a person should look' : ''}>
            {label} {k && n(type, k) != null && <span style={chipCountStyle()}>{n(type, k)}</span>}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {isAdmin && (
          <>
            <Btn size="sm" variant="outline" disabled={scanning} onClick={rescan}
              title="Refresh the queue now">{scanning ? 'Scanning…' : 'Rescan'}</Btn>
            <Btn size="sm" variant="accent" disabled={!n(type, 'strong')} onClick={openBulk}
              title="Review the strong pairs, untick any you don't want, then merge them all">
              Merge all strong{n(type, 'strong') ? ` (${n(type, 'strong')})` : ''}
            </Btn>
          </>
        )}
      </div>

      {items === null && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
      {items && items.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No duplicate {many} waiting{strength ? ' at this strength' : ''}.</div>
      )}
      {items && items.map((p) => (
        <div key={p.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: 'var(--surface-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            <Pill hue={p.strength === 'strong' ? 'green' : 'amber'} sz="sm">{p.strength === 'strong' ? 'strong' : 'worth a look'}</Pill>
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{(p.reasons || [p.reason]).map((r) => DEDUP_REASON[r] || r).join(' · ')}</span>
            <div style={{ flex: 1 }} />
            <Btn size="xs" variant="outline" disabled={busy === p.id} onClick={() => dismiss(p)}
              title="Remembered — this pair won't come back">Not duplicates</Btn>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {[[p.a, p.b], [p.b, p.a]].map(([rec, other]) => (
              <DedupSide key={rec.id} type={type} rec={rec} suggested={p.suggested_keep_id === rec.id} busy={busy === p.id}
                onOpen={() => open(rec)} onKeep={() => keep(p, rec, other)} />
            ))}
          </div>
        </div>
      ))}

      <Drawer open={!!bulk} onClose={() => !(bulk && bulk.running) && setBulk(null)} width={620}
        title={`Merge all strong ${many}`} subtitle="Untick any pair you don't want merged"
        footer={bulk && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
              {bulk.results ? `${bulk.results.filter((r) => r.ok).length} merged · ${bulk.results.filter((r) => !r.ok).length} skipped`
                : `${bulk.pairs.length - bulk.off.size} of ${bulk.pairs.length} ticked`}
            </span>
            <div style={{ flex: 1 }} />
            {bulk.results ? <Btn size="sm" variant="accent" onClick={() => setBulk(null)}>Done</Btn> : (
              <>
                <Btn size="sm" variant="outline" disabled={bulk.running} onClick={() => setBulk(null)}>Cancel</Btn>
                <Btn size="sm" variant="accent" disabled={bulk.running || bulk.pairs.length === bulk.off.size} onClick={runBulk}>
                  {bulk.running ? 'Merging…' : `Merge ${bulk.pairs.length - bulk.off.size}`}
                </Btn>
              </>
            )}
          </div>
        )}>
        {bulk && (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {bulk.pairs.map((p) => {
              const keepRec = p.suggested_keep_id === p.b.id ? p.b : p.a;
              const gone = keepRec === p.a ? p.b : p.a;
              const res = bulk.results && bulk.results.find((r) => r.id === p.id);
              return (
                <label key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, padding: '6px 0',
                                           borderBottom: '1px solid var(--border-2, var(--border))', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!bulk.off.has(p.id)} disabled={bulk.running || !!bulk.results}
                    style={{ accentColor: 'var(--accent)', marginTop: 2 }}
                    onChange={() => setBulk((b) => { const off = new Set(b.off); off.has(p.id) ? off.delete(p.id) : off.add(p.id); return { ...b, off }; })} />
                  <span style={{ flex: 1, lineHeight: 1.5 }}>
                    <b>{keepRec.title}</b>{keepRec.dealcloud_id ? ` (${keepRec.dealcloud_id})` : ''} ← {gone.title}{gone.dealcloud_id ? ` (${gone.dealcloud_id})` : ''}
                    <span style={{ color: 'var(--text-3)' }}> · {(p.reasons || [p.reason]).map((r) => DEDUP_REASON[r] || r).join(' · ')}</span>
                    {res && <span style={{ color: res.ok ? 'var(--green-fg, #067647)' : 'var(--red-fg, #b42318)' }}> · {res.ok ? 'merged' : res.error}</span>}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </Drawer>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
const chipStyle = (active) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '3px 9px', height: 22, borderRadius: 5,
  background: active ? '#fff' : 'transparent',
  border: active ? '1px solid var(--border)' : '1px solid transparent',
  boxShadow: active ? 'var(--shadow-sm)' : 'none',
  fontSize: 11.5, fontWeight: active ? 600 : 500,
  color: active ? 'var(--text)' : 'var(--text-3)',
  cursor: 'pointer', whiteSpace: 'nowrap',
});
const chipCountStyle = () => ({ fontSize: 10.5, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' });

const CleanupRow = ({ item, isFirst, onOpen }) => {
  const sig = item.signals || {};
  const reached = ['intro_or_ds_call','pb_blast','bs_call','nda_signed','loi','closing','won']
    .filter((k) => sig[k]?.reached);

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 14, padding: '12px 14px',
      borderTop: isFirst ? 'none' : '1px solid var(--border)',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => onOpen(item)} style={{
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            fontSize: 13, fontWeight: 600, color: 'var(--text)', textAlign: 'left',
          }}>
            {item.name}
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-4)' }}>
            {item.entity_type === 'company' ? 'Company' : 'Engagement'}
          </span>
          {item.current_state && (
            <Pill hue={item.entity_type === 'company' ? 'slate' : 'teal'} sz="sm">
              {item.current_state}
            </Pill>
          )}
        </div>

        {/* Reasons */}
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {item.reasons.map((r) => (
            <div key={r.reason_code} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Pill hue={REASON_HUE[r.reason_code] || 'slate'} sz="sm">
                {r.severity === 3 ? '!!' : r.severity === 2 ? '!' : 'ℹ'}
              </Pill>
              <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.reason_label}</span>
              {r.suggestion && (
                <span style={{ fontSize: 11, color: 'var(--text-4)', fontStyle: 'italic' }}>
                  — {r.suggestion}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Signal chips for context */}
        {reached.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {reached.map((k) => (
              <span key={k} style={{
                fontSize: 10.5, padding: '1px 6px', borderRadius: 3,
                background: 'var(--green-bg)', color: 'var(--green-fg)', fontWeight: 500,
              }}>
                ✓ {k.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
      </div>

      <button onClick={() => onOpen(item)} style={{
        background: 'transparent', border: '1px solid var(--border)', borderRadius: 5,
        padding: '4px 10px', cursor: 'pointer', fontSize: 11.5, color: 'var(--text-2)',
        height: 26, flexShrink: 0,
      }}>
        Open →
      </button>
    </div>
  );
};

window.CleanupView = CleanupView;

// ─────────────────────────────────────────────────────────────────────────
// Industries tab — AI-suggested industry for a company that has none.
//
// A company with no industry is invisible to buyer matching, so this queue
// sits upstream of the Buyers page's Match tab working at all. Unlike the
// other Cleanup tabs this one is STATEFUL: rejecting a suggestion has to
// stick, or the next sweep asks the same question forever.
//
// The row leads with the EVIDENCE — the sentence from the company's own site —
// because that is what makes a decision possible in one glance. Every accept
// writes companies.industry_id audited to the person who approved it; nothing
// here applies itself, however confident the model was.
// Why a verdict is worth less than its confidence number claims (145). These
// are the reasons a reviewer needs stated in words — "0.95 sure" off one
// unfinished page is not a lie about the model's certainty, it is a lie about
// the evidence, and only this can say so.
const EVIDENCE_FLAG = {
  single_page: { hue: 'amber', label: 'Read only 1 page',
    title: 'Only the homepage could be read, so "none of our industries fit" '
         + 'rests on a single page.' },
  placeholder_content: { hue: 'rose', label: 'Page looks unfinished',
    title: 'The page carried placeholder copy (lorem ipsum, "coming soon"). '
         + 'An unfinished website is not evidence that a business does not exist.' },
  thin_content: { hue: 'amber', label: 'Almost no content',
    title: 'The pages we read were nearly empty.' },
  site_mismatch: { hue: 'rose', label: 'Site may not be this company',
    title: 'The website read plausibly belongs to a different organisation — '
         + 'the address on the company record is probably wrong. Nothing was '
         + 'changed.' },
};
// The research drawer's industry adapter (research.jsx) labels its steps with these.
window.INDUSTRY_EVIDENCE_FLAG = EVIDENCE_FLAG;

// "What we read" — the disclosure that answers "why did it say that?" without
// anyone re-scraping the site by hand. Lazy on purpose: the excerpts are a few
// KB per proposal and the queue is 300 rows.
const IndustryEvidence = ({ proposalId }) => {
  const [open, setOpen] = React.useState(false);
  const [ev, setEv] = React.useState(null);   // null = not loaded, false = failed

  React.useEffect(() => {
    if (!open || ev !== null) return;
    window.AtlasApi.industryProposalEvidence(proposalId)
      .then((r) => setEv(r || false))
      .catch(() => setEv(false));
  }, [open, ev, proposalId]);

  const pages = (ev && ev.pages) || [];
  return (
    <details onToggle={(e) => setOpen(e.currentTarget.open)} style={{ marginTop: 5 }}>
      <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--text-4)' }}>
        What we read
      </summary>
      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-3)', lineHeight: 1.6 }}>
        {ev === null && <span>Loading…</span>}
        {ev === false && <span>Nothing was recorded for this reading.</span>}
        {ev && (
          <>
            <div style={{ color: 'var(--text-4)', marginBottom: 6 }}>
              {pages.length} page(s)
              {ev.read_at ? ` · ${new Date(ev.read_at).toLocaleDateString()}` : ''}
              {ev.evidence_quality != null
                ? ` · evidence quality ${Math.round(ev.evidence_quality * 100)}%` : ''}
            </div>
            {/* Rows written before 145 know WHICH pages were read but not what
                was on them. Saying that beats an empty panel that reads as a
                bug — and it is the honest answer for every proposal already
                stored. */}
            {!ev.has_excerpts && (
              <div style={{ marginBottom: 6, color: 'var(--amber-fg)' }}>
                This proposal was made before we started keeping page text, so only
                the URLs survive.
              </div>
            )}
            {pages.map((pg, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <a href={pg.url} target="_blank" rel="noreferrer"
                  style={{ color: 'var(--text-4)', wordBreak: 'break-all' }}>
                  {pg.url} ↗
                </a>
                {pg.excerpt && (
                  <pre className="mono" style={{
                    margin: '3px 0 0', padding: '6px 8px', background: 'var(--surface-2)',
                    border: '1px solid var(--border)', borderRadius: 5, fontSize: 10.5,
                    whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto',
                  }}>
                    {pg.excerpt}{pg.truncated ? '\n…' : ''}
                  </pre>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </details>
  );
};

const IndustryProposalsPane = ({ setCompanyId }) => {
  const [data, setData]   = React.useState(null);
  const [busy, setBusy]   = React.useState(null);
  const [showDone, setShowDone] = React.useState(false);
  // Per-row industry override, keyed by proposal id.
  const [override, setOverride] = React.useState({});

  const reload = React.useCallback(() => {
    setData(null);
    window.AtlasApi.industryProposals({ status: showDone ? 'all' : 'pending', limit: 300 })
      .then((r) => setData(r || { items: [] }))
      .catch(() => setData({ items: [] }));
  }, [showDone]);
  React.useEffect(() => { reload(); }, [reload]);

  const decide = async (p, action) => {
    setBusy(p.id);
    try {
      const body = { action };
      if (action === 'accept' && override[p.id]) body.industry_id = override[p.id];
      await window.AtlasApi.decideIndustryProposal(p.id, body);
      // Drop the row rather than refetching — the decision is final and a
      // refetch would jump the reviewer's place in a 300-row queue. The
      // header count comes from the server, so decrement it here too or it
      // keeps claiming work that is already done.
      setData((d) => ({
        ...d,
        items: (d.items || []).filter((x) => x.id !== p.id),
        pending_total: Math.max(0, (d.pending_total || 0) - 1),
      }));
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not save that decision',
                  body: String((e && e.message) || e) });
    } finally {
      setBusy(null);
    }
  };

  const items = (data && data.items) || [];
  const industries = (data && data.industries) || [];

  if (data === null) {
    return <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>;
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
          {data.pending_total > 0
            ? `${data.pending_total.toLocaleString()} awaiting a decision`
            : 'Nothing awaiting a decision'}
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--text-3)' }}>
          <input type="checkbox" checked={showDone}
            onChange={(e) => setShowDone(e.target.checked)} />
          Include decided
        </label>
      </div>

      {!items.length && (
        <div style={{
          background: 'var(--surface)', border: '1px dashed var(--border-strong)', borderRadius: 10,
          padding: '32px 24px', maxWidth: 680, fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.7,
        }}>
          No industry suggestions to review.
        </div>
      )}

      {items.length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          {items.map((p, i) => (
            <div key={p.id} style={{
              padding: '11px 14px', display: 'flex', gap: 12, alignItems: 'flex-start',
              borderTop: i === 0 ? 'none' : '1px solid var(--border)',
              opacity: p.status === 'pending' ? 1 : 0.55,
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <a href={`#/companies/${p.company_id}`}
                    onClick={(e) => { e.preventDefault(); setCompanyId && setCompanyId(p.company_id); }}
                    style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', textDecoration: 'none' }}>
                    {p.company_name}
                  </a>
                  {p.deal_stage && <Pill hue="slate" sz="sm">{p.deal_stage}</Pill>}
                  {p.website && (
                    <a href={p.source_url || p.website} target="_blank" rel="noreferrer"
                      style={{ fontSize: 11, color: 'var(--text-4)' }}>
                      {p.website.replace(/^https?:\/\/(www\.)?/, '')} ↗
                    </a>
                  )}
                </div>

                <div style={{ fontSize: 12.5, marginTop: 4, display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                  {/* Almost always absent — the queue is built from companies
                      with no industry. When it IS set, the proposal has gone
                      stale and accepting would overwrite a real value, so this
                      doubles as the warning. Decided rows already show what
                      was applied, so only pending rows need it. */}
                  {p.status === 'pending' && p.current_industry && (
                    <Pill hue="amber" sz="sm">
                      Currently {p.current_industry} — accepting replaces it
                    </Pill>
                  )}
                  {p.suggested_industry ? (
                    <>
                      <span style={{ color: 'var(--text-3)' }}>Suggested:</span>
                      <b style={{ color: 'var(--text)' }}>{p.suggested_industry}</b>
                    </>
                  ) : (
                    // A null suggestion is an ANSWER, not a gap: the site was
                    // read and none of the 41 industries fit.
                    <span style={{ color: 'var(--text-3)' }}>
                      <b>none of our industries fit</b>
                    </span>
                  )}
                  {/* The MODEL's number capped by how much it was actually
                      shown, because the two are different questions and only
                      the first one used to be visible. Where they differ the
                      title says both — a reviewer looking at "35% sure" on a
                      verdict the model called 0.95 deserves to know why. */}
                  {p.review_confidence != null && (
                    <Pill sz="sm"
                      hue={p.review_confidence >= 0.85 ? 'green'
                        : p.review_confidence >= 0.6 ? 'amber' : 'slate'}>
                      {Math.round(p.review_confidence * 100)}% sure
                    </Pill>
                  )}
                  {(p.evidence_flags || []).map((f) => {
                    const meta = EVIDENCE_FLAG[f];
                    return (
                      <Pill key={f} hue={meta ? meta.hue : 'slate'} sz="sm"
                        title={meta ? meta.title : undefined}>
                        {meta ? meta.label : f}
                      </Pill>
                    );
                  })}
                  {p.status !== 'pending' && (
                    <Pill hue={p.status === 'accepted' ? 'teal' : 'slate'} sz="sm">
                      {p.status}{p.applied_industry ? ` · ${p.applied_industry}` : ''}
                      {p.decided_by_name ? ` · ${p.decided_by_name}` : ''}
                    </Pill>
                  )}
                </div>

                {/* The evidence is the point of the row — it is what lets a
                    reviewer decide without opening the site. */}
                {p.evidence && (
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 4,
                                lineHeight: 1.55, fontStyle: 'italic' }}>
                    “{p.evidence}”
                  </div>
                )}

                {/* One sentence of evidence is enough to clear the easy rows
                    and not enough to settle a dispute. This is the second
                    click: the page text the model was actually looking at. */}
                <IndustryEvidence proposalId={p.id} />
              </div>

              {p.status === 'pending' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  {/* Override, so a wrong suggestion still gets fixed here
                      instead of sending the reviewer to the company page. */}
                  <Combobox
                    value={override[p.id] || p.suggested_industry_id || ''}
                    onChange={(e) => setOverride((o) => ({ ...o, [p.id]: e.target.value }))}
                    options={[{ value: '', label: 'Pick an industry…' },
                      ...industries.map((x) => ({ value: x.id, label: x.name }))]}
                    style={{ minWidth: 190 }}
                  />
                  <Btn size="xs" variant="accent" disabled={busy === p.id}
                    onClick={() => decide(p, 'accept')}>
                    {busy === p.id ? '…' : 'Accept'}
                  </Btn>
                  <Btn size="xs" variant="ghost" disabled={busy === p.id}
                    onClick={() => decide(p, 'reject')}>
                    Reject
                  </Btn>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Blast hygiene — contacts on a broadcast list who should not be mailed.
//
// Read-only by design. Atlas cannot remove a DealCloud list tag, so the fix
// is Krisztian's in DC; what Atlas can do is say exactly who, and stop
// showing them once the hourly sync mirrors the tag removal back. That is
// also why there is no "dismiss": dismissing here while the contact is still
// on the list would hide the exact failure that matters.
const BlastHygienePane = ({ setContactId }) => {
  const [data, setData] = React.useState(null);
  const [list, setList] = React.useState('');

  React.useEffect(() => {
    setData(null);
    window.AtlasApi.blastHygiene(list ? { list, limit: 300 } : { limit: 300 })
      .then((r) => setData(r || { items: [] }))
      .catch(() => setData({ items: [] }));
  }, [list]);

  if (data === null) {
    return <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>;
  }
  const items = data.items || [];
  const totals = data.totals || {};

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
          <b style={{ color: 'var(--text)' }}>{(data.total_contacts || 0).toLocaleString()}</b>{' '}
          contact(s) are on a broadcast list but tagged bounced, unsubscribed or opted-out
        </span>
        <div style={{ flex: 1 }} />
        {/* Export, because the fix happens in DealCloud — a list you can
            paste into a DC filter is more useful than a page of rows. */}
        <window.ExportMenu
          disabled={!items.length}
          viewCount={items.length}
          onExport={(format) => {
            const cols = [
              { key: 'name', label: 'Contact' }, { key: 'email', label: 'Email' },
              { key: 'company_name', label: 'Company' },
              { key: 'lists', label: 'On lists' }, { key: 'reasons', label: 'Why not to mail' },
            ];
            const records = items.map((c) => ({
              name: c.name, email: c.email || '', company_name: c.company_name || '',
              lists: (c.lists || []).join('; '), reasons: (c.reasons || []).join('; '),
            }));
            const nm = `blast-hygiene-${window.localISODate()}`;
            if (format === 'csv') window.exportCsv(`${nm}.csv`, cols, records);
            else window.exportJson(`${nm}.json`, records);
          }}
        />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
        <button onClick={() => setList('')} style={chipStyle(list === '')}>
          All <span style={chipCountStyle()}>{(data.total_contacts || 0).toLocaleString()}</span>
        </button>
        {Object.entries(totals).sort((a, b) => b[1] - a[1]).map(([name, n]) => (
          <button key={name} onClick={() => setList(name === list ? '' : name)}
            style={chipStyle(list === name)}>
            {name} <span style={chipCountStyle()}>{n.toLocaleString()}</span>
          </button>
        ))}
      </div>

      {!items.length && (
        <div style={{
          background: 'var(--surface)', border: '1px dashed var(--border-strong)', borderRadius: 10,
          padding: '32px 24px', maxWidth: 640, fontSize: 12.5, color: 'var(--text-3)',
        }}>
          Nothing to clean — nobody on a broadcast list is flagged as unmailable.
        </div>
      )}

      {items.length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          {items.map((c, i) => (
            <div key={c.contact_id} style={{
              padding: '9px 14px', display: 'flex', gap: 10, alignItems: 'flex-start',
              borderTop: i === 0 ? 'none' : '1px solid var(--border)',
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <a href={`#/people/${c.contact_id}`}
                    onClick={(e) => { e.preventDefault(); setContactId && setContactId(c.contact_id); }}
                    style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', textDecoration: 'none' }}>
                    {c.name}
                  </a>
                  {c.email && <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{c.email}</span>}
                  {c.company_name && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{c.company_name}</span>}
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                  {(c.lists || []).map((l) => <Pill key={l} hue="amber" sz="sm">{l}</Pill>)}
                  {(c.reasons || []).map((r) => <Pill key={r} hue="rose" sz="sm">{r}</Pill>)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};


// ── Stale deals — the book-cleanup work surface ─────────────────────────────
// Every live seller deal past the D/S call that the dealmaker daily report
// flags (quiet / stuck / no next action / overdue next action), filterable by
// owner, age band and flag, with bulk hold / exit / next action. Each bulk
// action goes through the normal engagement PATCH on the server (audit row,
// stage history, DealCloud write-back), one deal at a time — nothing is
// bypassed, which is why a big selection takes a moment.

const SD_FLAG = {
  quiet:          { label: 'quiet',          hue: 'rose',  hint: 'No note from anyone on the company in 30+ days' },
  stuck:          { label: 'stuck',          hue: 'amber', hint: 'Older in its stage than 2× the stage median (min 30 days)' },
  no_next_action: { label: 'no next action', hue: 'amber', hint: 'next_action_date is empty' },
  overdue_na:     { label: 'overdue NA',     hue: 'rose',  hint: 'Next action date is in the past' },
};
const SD_BANDS = [['30-90', '30–90 days'], ['90-180', '90–180 days'], ['180+', '180+ days']];
const sdMoney = (v) => (v == null ? '' : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${Math.round(v / 1e3)}k` : `$${Math.round(v)}`);
const HOLD_LABEL = { high: 'High priority', low: 'Low priority', repricing: 'Repricing' };
const HOLD_REASON_LABEL_SD = { owner_delay: 'Owner delay', business_delay: 'Business delay' };
// Filter chip / input / select shared by the Stale deals and Holds panes.
const sdChip = (active, label, onClick, hue, key) => (
  <button key={key || label} onClick={onClick} style={{
    padding: '3px 9px', borderRadius: 999, fontSize: 11.5, cursor: 'pointer',
    border: `1px solid ${active ? 'var(--text)' : 'var(--border)'}`,
    background: active ? 'var(--text)' : 'var(--surface)', color: active ? 'var(--bg)' : 'var(--text-2)',
  }}>{label}</button>
);
const sdInput = (props) => (
  <input {...props} style={{ fontSize: 12.5, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 5, background: 'var(--surface)', color: 'var(--text)', ...(props.style || {}) }} />
);
// options: [value, label, hint?]
const sdSelect = (value, onChange, options, placeholder) => (
  <window.Picker value={value || ''} onChange={(v) => onChange(v || null)} emptyLabel={placeholder} placeholder={placeholder}
    minWidth={160} menuMinWidth={260} options={options.map(([v, l, hint]) => ({ value: v, label: l, hint }))} />
);

const StaleDealsPane = ({ setDealId }) => {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [owner, setOwner] = React.useState(null);        // dealmaker_id | null
  const [band, setBand] = React.useState(null);          // '30-90' | '90-180' | '180+' | null
  const [flag, setFlag] = React.useState(null);          // flag key | null
  const [flaggedOnly, setFlaggedOnly] = React.useState(true);
  const [sel, setSel] = React.useState(() => new Set());
  const [action, setAction] = React.useState(null);      // 'hold' | 'exit' | 'next_action' | null
  const [form, setForm] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const load = React.useCallback(() => {
    setErr('');
    window.AtlasApi.staleDeals().then((d) => { setData(d); setSel(new Set()); })
      .catch((e) => setErr(e?.detail || e?.message || 'Failed to load'));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const rows = React.useMemo(() => {
    if (!data) return [];
    return data.deals.filter((d) =>
      (!flaggedOnly || d.flags.length > 0)
      && (!owner || d.dealmaker_id === owner)
      && (!band || d.band === band)
      && (!flag || d.flags.includes(flag)));
  }, [data, owner, band, flag, flaggedOnly]);
  const allSelected = rows.length > 0 && rows.every((d) => sel.has(d.id));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(rows.map((d) => d.id)));
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selected = rows.filter((d) => sel.has(d.id));
  const selFees = selected.reduce((a, d) => a + (d.fees || 0), 0);

  // Exit reasons available to EVERY selected deal (per-stage catalogue) — the
  // server skips deals whose stage lacks the slug; we show how many would apply.
  const exitCoverage = React.useMemo(() => {
    const out = {};
    (data?.exit_reasons || []).forEach((x) => { out[x.slug] = selected.filter((d) => d.exit_slugs.includes(x.slug)).length; });
    return out;
  }, [data, selected]);

  const run = async () => {
    if (!action || !selected.length) return;
    const body = { ids: selected.map((d) => d.id), action, ...form };
    const label = action === 'hold' ? `put ${selected.length} deal${selected.length === 1 ? '' : 's'} on hold`
      : action === 'exit' ? `exit ${exitCoverage[form.exit_slug] || 0} deal${(exitCoverage[form.exit_slug] || 0) === 1 ? '' : 's'} as "${(data.exit_reasons.find((x) => x.slug === form.exit_slug) || {}).name || form.exit_slug}"`
        : `set a next action on ${selected.length} deal${selected.length === 1 ? '' : 's'}`;
    const ok = await window.showConfirm({
      title: 'Apply to selected deals?',
      body: `This will ${label} (${sdMoney(selFees) || '$0'} in estimated fees).`,
      confirmLabel: 'Apply', danger: action === 'exit',
    });
    if (!ok) return;
    setBusy(true); setResult(null);
    try {
      const r = await window.AtlasApi.staleDealsBulk(body);
      setResult(r);
      setAction(null); setForm({});
      load();
    } catch (e) { setErr(e?.detail || e?.message || 'Bulk action failed'); }
    finally { setBusy(false); }
  };

  const chip = sdChip;
  const input = sdInput;
  const select = sdSelect;

  if (err) return <div style={{ fontSize: 12.5, color: HUE.rose.dot }}>{err}</div>;
  if (!data) return <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>;

  const canRun = !busy && selected.length > 0 && (
    (action === 'hold' && form.on_hold)
    || (action === 'exit' && form.exit_slug && (exitCoverage[form.exit_slug] || 0) > 0)
    || (action === 'next_action' && form.next_action_date));

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 10, maxWidth: 860, lineHeight: 1.5 }}>
        <b>{data.flagged}</b> of {data.total} worked deals flagged,
        {' '}<b>{sdMoney(data.fees_flagged) || '$0'}</b> in estimated fees.
        <window.HelpTip text="Live seller deals past the D/S call that the daily report flags. Buyer-side shells and deals already on hold are not shown." />
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', width: 52 }}>Owner</span>
        {chip(!owner, `All (${data.flagged})`, () => setOwner(null))}
        {data.owners.filter((o) => o.flagged > 0 || o.id === owner).map((o) => chip(owner === o.id, `${o.name} (${o.flagged}${o.fees_flagged ? ` · ${sdMoney(o.fees_flagged)}` : ''})`, () => setOwner(owner === o.id ? null : o.id)))}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', width: 52 }}>Age</span>
        {chip(!band, 'Any', () => setBand(null))}
        {SD_BANDS.map(([k, l]) => chip(band === k, `${l} (${data.bands[k] || 0})`, () => setBand(band === k ? null : k)))}
        <span style={{ width: 14 }} />
        <span style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Flag</span>
        {chip(!flag, 'Any', () => setFlag(null))}
        {Object.entries(SD_FLAG).map(([k, f]) => chip(flag === k, `${f.label} (${data.flags[k] || 0})`, () => setFlag(flag === k ? null : k)))}
        <span style={{ width: 14 }} />
        {chip(!flaggedOnly, 'include unflagged', () => setFlaggedOnly((v) => !v))}
      </div>

      {result && (
        <div style={{ fontSize: 12.5, color: 'var(--text-2)', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
          Updated {result.updated} deal{result.updated === 1 ? '' : 's'}.
          {result.skipped.length > 0 && <span style={{ color: HUE.amber.dot }}> Skipped {result.skipped.length}: {result.skipped.slice(0, 4).map((x) => `${x.name || x.id} (${x.reason})`).join('; ')}{result.skipped.length > 4 ? '…' : ''}</span>}
          {result.errors.length > 0 && <span style={{ color: HUE.rose.dot }}> Failed {result.errors.length}: {result.errors.slice(0, 3).map((x) => `${x.name || x.id} (${x.reason})`).join('; ')}</span>}
        </div>
      )}

      {/* Action bar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', marginBottom: 10, minHeight: 44 }}>
        <span style={{ fontSize: 12.5, color: selected.length ? 'var(--text)' : 'var(--text-4)', fontWeight: 600 }}>
          {selected.length ? `${selected.length} selected · ${sdMoney(selFees) || '$0'}` : 'Select deals below'}
        </span>
        <div style={{ flex: 1 }} />
        {[['hold', 'Put on hold'], ['exit', 'Exit as…'], ['next_action', 'Set next action']].map(([k, l]) => (
          <Btn key={k} size="sm" variant={action === k ? 'outline' : 'ghost'} disabled={!selected.length || busy} onClick={() => { setAction(action === k ? null : k); setForm({}); }}>{l}</Btn>
        ))}
        {action === 'hold' && (
          <>
            {select(form.on_hold, (v) => setForm((f) => ({ ...f, on_hold: v })), Object.entries(HOLD_LABEL), 'Hold priority…')}
            {select(form.on_hold_reason, (v) => setForm((f) => ({ ...f, on_hold_reason: v })), Object.entries(HOLD_REASON_LABEL_SD), 'Delay type (optional)')}
          </>
        )}
        {action === 'exit' && (
          <>
            {select(form.exit_slug, (v) => setForm((f) => ({ ...f, exit_slug: v })),
              data.exit_reasons.map((x) => [x.slug, `${x.name}${x.is_recoverable ? '' : ' (final)'} — applies to ${exitCoverage[x.slug] || 0}/${selected.length}`]), 'Exit reason…')}
            {input({ placeholder: 'Note (optional)', value: form.exit_notes || '', onChange: (e) => setForm((f) => ({ ...f, exit_notes: e.target.value })), style: { width: 220 } })}
          </>
        )}
        {action === 'next_action' && (
          <>
            {input({ type: 'date', value: form.next_action_date || '', onChange: (e) => setForm((f) => ({ ...f, next_action_date: e.target.value })) })}
            {input({ placeholder: 'What happens next (optional)', value: form.next_action_notes || '', onChange: (e) => setForm((f) => ({ ...f, next_action_notes: e.target.value })), style: { width: 240 } })}
          </>
        )}
        {action && <Btn size="sm" variant={action === 'exit' ? 'primary' : 'accent'} disabled={!canRun} onClick={run}>{busy ? 'Applying…' : 'Apply'}</Btn>}
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={{ padding: '7px 10px', width: 28 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} title="Select all shown" /></th>
              {['Deal', 'Owner', 'Stage', 'Fees', 'Last note', 'In stage', 'Next action', 'Flags'].map((h) => (
                <th key={h} style={{ textAlign: ['Fees', 'Last note', 'In stage'].includes(h) ? 'right' : 'left', padding: '7px 10px', fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} style={{ padding: 14, color: 'var(--text-3)' }}>Nothing matches — a clean book, or loosen the filters.</td></tr>}
            {rows.map((d) => (
              <tr key={d.id} style={{ borderTop: '1px solid var(--border)', background: sel.has(d.id) ? 'var(--surface-2)' : 'transparent' }}>
                <td style={{ padding: '6px 10px' }}><input type="checkbox" checked={sel.has(d.id)} onChange={() => toggle(d.id)} /></td>
                <td style={{ padding: '6px 10px', maxWidth: 280 }}>
                  <span onClick={() => setDealId?.(d.id)} style={{ color: 'var(--accent-text)', cursor: 'pointer', fontWeight: 600 }}>{d.name}</span>
                  {d.company && d.company !== d.name && <span style={{ color: 'var(--text-4)', fontSize: 11 }}> · {d.company}</span>}
                </td>
                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: 'var(--text-2)' }}>{d.dealmaker}</td>
                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}><Pill hue="slate" sz="sm">{d.stage}</Pill></td>
                <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: d.fees ? 'var(--text)' : 'var(--text-4)' }}>{sdMoney(d.fees) || '—'}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: (d.days_quiet || 0) >= 180 ? HUE.rose.dot : (d.days_quiet || 0) >= 30 ? HUE.amber.dot : 'var(--text-3)' }} title={d.last_touch ? `Last note ${window.fmtDate(d.last_touch)}` : 'No note ever'}>{d.last_touch ? `${d.days_quiet}d` : 'never'}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: d.flags.includes('stuck') ? HUE.rose.dot : 'var(--text-3)' }} title={d.stage_median_days != null ? `Stage median ${d.stage_median_days}d` : ''}>{d.days_in_stage}d</td>
                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: d.next_action_date ? 'var(--text-2)' : 'var(--text-4)' }} title={d.next_action_notes || ''}>{d.next_action_date ? window.fmtDate(d.next_action_date) : '—'}</td>
                <td style={{ padding: '6px 10px' }}>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {d.flags.map((f) => <Pill key={f} hue={SD_FLAG[f]?.hue || 'slate'} sz="sm" title={SD_FLAG[f]?.hint}>{SD_FLAG[f]?.label || f}</Pill>)}
                    {d.flags.length === 0 && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>—</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Holds — open on-hold seller deals nobody is working ─────────────────────
// Every open on-hold seller deal (app/stale_holds.py) with its last two-way
// and outbound touch and the first ACTIVE owner in dealmaker → company owner
// → industry lead. 'Stale' and 'No active owner' are the two cleanup lists
// Andres asked for (2026-10-13); the Monday DM (app/holds_weekly.py) links
// here as #/cleanup?tab=holds&scope=no_owner. Bulk exit / next action go
// through the same per-deal engagement PATCH as Stale deals.

const HOLD_SCOPES = [['stale', 'Stale'], ['no_owner', 'No active owner'], ['all', 'All holds']];
const HOLD_PRIORITY_LABEL = { high: 'High', low: 'Low', repricing: 'Repricing' };
const HOLD_FLAG = {
  stale:    { label: 'stale',    hue: 'rose' },
  no_owner: { label: 'no owner', hue: 'amber' },
};
const HOLD_HELP = {
  held:     'Last time the deal was put on hold in Atlas; before that, the hold date from DealCloud; else its last stage change.',
  twoWay:   "Latest email from a contact at the company, or a call or meeting with them. Internal-only and future-dated events don't count.",
  outbound: 'Latest email sent to the company, or a call or meeting with them.',
  owner:    'Dealmaker, else company owner, else industry lead. Active members only.',
  stale:    'No two-way touch in 540 days (high, repricing) or 730 days (low), or none on record.',
};
const HOLDS_PAGE = 300;
const holdScopeFromHash = () => {
  const m = /[?&]scope=([a-z_]+)/.exec(window.location.hash || '');
  return m && HOLD_SCOPES.some(([k]) => k === m[1]) ? m[1] : 'stale';
};
const holdAge = (n) => (n == null ? 'never' : n < 60 ? `${n}d` : n < 730 ? `${Math.round(n / 30)} mo` : `${(n / 365).toFixed(1)} yr`);

const HoldsPane = ({ setDealId, canEdit }) => {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [scope, setScope] = React.useState(holdScopeFromHash);   // 'stale' | 'no_owner' | 'all'
  const [owner, setOwner] = React.useState(null);                // member id | 'none' | null
  const [priority, setPriority] = React.useState(null);          // 'high' | 'low' | 'repricing' | null
  const [delay, setDelay] = React.useState(null);                // 'owner_delay' | 'business_delay' | 'none' | null
  const [sel, setSel] = React.useState(() => new Set());
  const [action, setAction] = React.useState(null);              // 'exit' | 'next_action' | null
  const [form, setForm] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [limit, setLimit] = React.useState(HOLDS_PAGE);

  const load = React.useCallback(() => {
    setErr('');
    window.AtlasApi.staleHolds()
      .then((d) => { setData(d && Array.isArray(d.deals) ? d : { deals: [] }); setSel(new Set()); })
      .catch((e) => setErr(e?.detail || e?.message || 'Failed to load'));
  }, []);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { setLimit(HOLDS_PAGE); }, [scope, owner, priority, delay]);

  const inScope = React.useMemo(
    () => (data ? data.deals.filter((d) => scope === 'all' || (d.flags || []).includes(scope)) : []),
    [data, scope]);
  const rows = React.useMemo(() => inScope.filter((d) =>
    (!owner || (owner === 'none' ? !d.owner_id : d.owner_id === owner))
    && (!priority || d.on_hold === priority)
    && (!delay || (delay === 'none' ? !d.on_hold_reason : d.on_hold_reason === delay))),
  [inScope, owner, priority, delay]);
  // Facet counts inside the current scope.
  const facets = React.useMemo(() => {
    const owners = {}, pri = {}, del = {};
    inScope.forEach((d) => {
      const k = d.owner_id || 'none';
      if (!owners[k]) owners[k] = { id: k, name: d.owner_id ? d.owner : 'No active owner', n: 0 };
      owners[k].n += 1;
      pri[d.on_hold] = (pri[d.on_hold] || 0) + 1;
      const r = d.on_hold_reason || 'none';
      del[r] = (del[r] || 0) + 1;
    });
    const list = Object.values(owners).sort((a, b) => (a.id === 'none' ? -1 : b.id === 'none' ? 1 : b.n - a.n || a.name.localeCompare(b.name)));
    return { owners: list, pri, del };
  }, [inScope]);
  const selected = React.useMemo(() => rows.filter((d) => sel.has(d.id)), [rows, sel]);
  const selFees = selected.reduce((a, d) => a + (d.fees || 0), 0);
  // How many selected deals sit at a stage that offers each exit reason.
  const exitCoverage = React.useMemo(() => {
    const out = {};
    const by = data?.exit_slugs_by_stage || {};
    (data?.exit_reasons || []).forEach((x) => { out[x.slug] = selected.filter((d) => (by[d.stage_id] || []).includes(x.slug)).length; });
    return out;
  }, [data, selected]);

  const allSelected = rows.length > 0 && rows.every((d) => sel.has(d.id));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(rows.map((d) => d.id)));
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const run = async () => {
    if (!action || !selected.length) return;
    const n = action === 'exit' ? (exitCoverage[form.exit_slug] || 0) : selected.length;
    const what = action === 'exit'
      ? `exit ${n} deal${n === 1 ? '' : 's'} as "${(data.exit_reasons.find((x) => x.slug === form.exit_slug) || {}).name || form.exit_slug}"`
      : `set a next action on ${n} deal${n === 1 ? '' : 's'}`;
    const ok = await window.showConfirm({
      title: 'Apply to selected holds?',
      body: `This will ${what} (${sdMoney(selFees) || '$0'} in estimated fees).`,
      confirmLabel: 'Apply', danger: action === 'exit',
    });
    if (!ok) return;
    setBusy(true); setResult(null);
    const ids = selected.map((d) => d.id);
    const out = { updated: 0, skipped: [], errors: [] };
    let sent = 0;
    try {
      // The server takes 200 ids per call; larger selections go in batches.
      for (let i = 0; i < ids.length; i += 200) {
        const r = await window.AtlasApi.staleHoldsBulk({ ids: ids.slice(i, i + 200), action, ...form });
        sent = Math.min(i + 200, ids.length);
        out.updated += r.updated || 0;
        out.skipped = out.skipped.concat(r.skipped || []);
        out.errors = out.errors.concat(r.errors || []);
      }
      setAction(null); setForm({});
    } catch (e) {
      // Shown in the result line: the reload below clears `err`.
      const why = String(e?.message || 'request failed').slice(0, 160);
      out.errors = [{ id: '', name: `${ids.length - sent} not confirmed`, reason: why }].concat(out.errors);
    }
    finally {
      setResult(out); setBusy(false);
      load();
    }
  };

  if (err) return <div style={{ fontSize: 12.5, color: HUE.rose.dot }}>{err}</div>;
  if (!data) return <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>;

  const canRun = !busy && selected.length > 0 && (
    (action === 'exit' && form.exit_slug && (exitCoverage[form.exit_slug] || 0) > 0)
    || (action === 'next_action' && form.next_action_date));
  const counts = data.counts || {};
  const facetLabel = (extra) => ({ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', ...(extra || {}) });
  const th = (h, help, align) => (
    <th key={h} style={{ textAlign: align || 'left', padding: '7px 10px', fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)', fontWeight: 600, whiteSpace: 'nowrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{h}{help && <window.HelpTip text={help} size={12} />}</span>
    </th>
  );
  const shown = rows.slice(0, limit);
  const cols = canEdit ? 9 : 8;

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        {HOLD_SCOPES.map(([k, l]) => sdChip(scope === k, `${l} (${counts[k] || 0})`, () => { setScope(k); setOwner(null); }))}
        <window.HelpTip text={HOLD_HELP.stale} size={12} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <span style={facetLabel({ width: 52 })}>Priority</span>
        {sdChip(!priority, 'Any', () => setPriority(null), null, 'priority-any')}
        {Object.entries(HOLD_PRIORITY_LABEL).filter(([k]) => facets.pri[k] || priority === k)
          .map(([k, l]) => sdChip(priority === k, `${l} (${facets.pri[k] || 0})`, () => setPriority(priority === k ? null : k)))}
        <span style={{ width: 14 }} />
        <span style={facetLabel()}>Delay</span>
        {sdChip(!delay, 'Any', () => setDelay(null), null, 'delay-any')}
        {[...Object.entries(HOLD_REASON_LABEL_SD), ['none', 'Not set']].filter(([k]) => facets.del[k] || delay === k)
          .map(([k, l]) => sdChip(delay === k, `${l} (${facets.del[k] || 0})`, () => setDelay(delay === k ? null : k)))}
        <span style={{ width: 14 }} />
        <span style={facetLabel()}>Owner</span>
        {sdSelect(owner, setOwner, facets.owners.map((o) => [o.id, o.name, o.n.toLocaleString()]), `Any owner (${inScope.length})`)}
      </div>

      {result && (
        <div style={{ fontSize: 12.5, color: 'var(--text-2)', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
          Updated {result.updated} deal{result.updated === 1 ? '' : 's'}.
          {result.skipped.length > 0 && <span style={{ color: HUE.amber.dot }}> Skipped {result.skipped.length}: {result.skipped.slice(0, 4).map((x) => `${x.name || x.id} (${x.reason})`).join('; ')}{result.skipped.length > 4 ? '…' : ''}</span>}
          {result.errors.length > 0 && <span style={{ color: HUE.rose.dot }}> Failed {result.errors.length}: {result.errors.slice(0, 3).map((x) => `${x.name || x.id} (${x.reason})`).join('; ')}</span>}
        </div>
      )}

      {canEdit && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', marginBottom: 10, minHeight: 44 }}>
          <span style={{ fontSize: 12.5, color: selected.length ? 'var(--text)' : 'var(--text-4)', fontWeight: 600 }}>
            {selected.length ? `${selected.length} selected · ${sdMoney(selFees) || '$0'}` : 'Select deals below'}
          </span>
          <div style={{ flex: 1 }} />
          {[['exit', 'Exit as…'], ['next_action', 'Set next action']].map(([k, l]) => (
            <Btn key={k} size="sm" variant={action === k ? 'outline' : 'ghost'} disabled={!selected.length || busy} onClick={() => { setAction(action === k ? null : k); setForm({}); }}>{l}</Btn>
          ))}
          {action === 'exit' && (
            <>
              {sdSelect(form.exit_slug, (v) => setForm((f) => ({ ...f, exit_slug: v })),
                (data.exit_reasons || []).map((x) => [x.slug, `${x.name}${x.is_recoverable ? '' : ' (final)'}`, `applies to ${exitCoverage[x.slug] || 0}/${selected.length}`]), 'Exit reason…')}
              {sdInput({ placeholder: 'Note (optional)', value: form.exit_notes || '', onChange: (e) => setForm((f) => ({ ...f, exit_notes: e.target.value })), style: { width: 220 } })}
            </>
          )}
          {action === 'next_action' && (
            <>
              {sdInput({ type: 'date', value: form.next_action_date || '', onChange: (e) => setForm((f) => ({ ...f, next_action_date: e.target.value })) })}
              {sdInput({ placeholder: 'What happens next (optional)', value: form.next_action_notes || '', onChange: (e) => setForm((f) => ({ ...f, next_action_notes: e.target.value })), style: { width: 240 } })}
            </>
          )}
          {action && <Btn size="sm" variant={action === 'exit' ? 'primary' : 'accent'} disabled={!canRun} onClick={run}>{busy ? 'Applying…' : 'Apply'}</Btn>}
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
          <thead>
            <tr>
              {canEdit && <th style={{ padding: '7px 10px', width: 28 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} title={`Select all ${rows.length}`} /></th>}
              {th('Deal')}
              {th('Hold')}
              {th('Held since', HOLD_HELP.held)}
              {th('Last two-way', HOLD_HELP.twoWay, 'right')}
              {th('Last outbound', HOLD_HELP.outbound, 'right')}
              {th('Owner', HOLD_HELP.owner)}
              {th('Fees', null, 'right')}
              {th('Flags', HOLD_HELP.stale)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={cols} style={{ padding: 14, color: 'var(--text-3)' }}>No holds match these filters.</td></tr>}
            {shown.map((d) => {
              const staleDays = (data.stale_after_days || {})[d.on_hold] || 540;
              return (
                <tr key={d.id} style={{ borderTop: '1px solid var(--border)', background: sel.has(d.id) ? 'var(--surface-2)' : 'transparent' }}>
                  {canEdit && <td style={{ padding: '6px 10px' }}><input type="checkbox" checked={sel.has(d.id)} onChange={() => toggle(d.id)} /></td>}
                  <td style={{ padding: '6px 10px', maxWidth: 280 }}>
                    <span onClick={() => setDealId?.(d.id)} style={{ color: 'var(--accent-text)', cursor: 'pointer', fontWeight: 600 }}>{d.name}</span>
                    {d.company && d.company !== d.name && <span style={{ color: 'var(--text-4)', fontSize: 11 }}> · {d.company}</span>}
                  </td>
                  <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                    <Pill hue="amber" sz="sm">{HOLD_PRIORITY_LABEL[d.on_hold] || d.on_hold}</Pill>
                    {d.on_hold_reason && <span style={{ color: 'var(--text-3)', fontSize: 11.5 }}> {HOLD_REASON_LABEL_SD[d.on_hold_reason] || d.on_hold_reason}</span>}
                  </td>
                  <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: 'var(--text-2)' }} title={d.days_held != null ? `${d.days_held}d` : ''}>{d.held_since ? window.fmtDate(d.held_since) : '—'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: d.days_two_way == null || d.days_two_way > staleDays ? HUE.rose.dot : d.days_two_way >= 180 ? HUE.amber.dot : 'var(--text-3)' }}
                    title={d.last_two_way ? window.fmtDate(d.last_two_way) : ''}>{holdAge(d.days_two_way)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)' }}
                    title={d.last_outbound ? window.fmtDate(d.last_outbound) : ''}>{holdAge(d.days_outbound)}</td>
                  <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                    {d.owner_id
                      ? <span style={{ color: 'var(--text-2)' }}>{d.owner}</span>
                      : <span style={{ color: HUE.amber.dot }}>No active owner</span>}
                    {d.dealmaker && !d.dealmaker_active && (
                      <div style={{ fontSize: 11, color: 'var(--text-4)', textDecoration: 'line-through' }} title="Inactive">{d.dealmaker}</div>
                    )}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: d.fees ? 'var(--text)' : 'var(--text-4)' }}>{sdMoney(d.fees) || '—'}</td>
                  <td style={{ padding: '6px 10px' }}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {d.flags.map((f) => <Pill key={f} hue={HOLD_FLAG[f]?.hue || 'slate'} sz="sm">{HOLD_FLAG[f]?.label || f}</Pill>)}
                      {d.flags.length === 0 && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>—</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length > limit && (
        <div style={{ marginTop: 10 }}>
          <Btn size="sm" variant="ghost" onClick={() => setLimit((n) => n + HOLDS_PAGE)}>Show {Math.min(HOLDS_PAGE, rows.length - limit)} more of {rows.length - limit}</Btn>
        </div>
      )}
    </div>
  );
};
